import PolyParameter from 'parameters/PolyParameter.js';
import OptionParameter from 'parameters/OptionParameter.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {getImaginaryReal, getImaginaryRealSync} from 'constants/waveforms.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import Tone from 'tone';

const PORTAMENTO_FACTOR = 20.0;

export default superclass => class extends superclass  {
    parameters = [];
    
    isEnabled() {
        return this.enabled;
    }

    setupParameters() {
        this.enabledParameter = new AbstractParameter(
            'Enabled', this,
            (o, x) => {},
            {
                documentation: [
                    "Turns the operator on/off.",
                    "If you`re running low on CPU, try turning off some operators."
                ],
                min: false,
                max: true,
                unit: '',
                defaultValue: true,
                automatable: false
            });

        this.registerParameter(this.enabledParameter);

        let oscName = this.name;
        this.voices.map(x => x.volume.value = 0);
        this.oscillatorLevelParameter = new PolyParameter(
            'Oscillator Level', this.voices.map(x => x.volume),
            {
                documentation: "Controls the volume for this operator",
                min: 0,
                max: 1,
                defaultValue: 0.3,
                onChange: (x) => {
                    if (this.instrument.controller) {
                        this.instrument.controller.sendOscillatorLevels();
                    }
                }
            });
        this.registerParameter(this.oscillatorLevelParameter);

        this.pwmParameter = new AbstractParameter(
            'PWM', this,
            (o, x) => this.voices.forEach(
                v => 0)
            ,
            {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: 0,
                automatable: false
            });

        this.registerParameter(this.pwmParameter);

        this.countParameter = new AbstractParameter(
            'count', this,
            (o, x) => this.voices.forEach(
                v => v.oscillator.count = x)
            ,
            {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: 3,
                automatable: false
            });

        this.registerParameter(this.countParameter);

        this.spreadParameter = new AbstractParameter(
            'spread', this,
            (o, x) => this.voices.forEach(
                v => v.oscillator.spread = x)
            ,
            {
                min: 1,
                max: 200,
                unit: '',
                defaultValue: 20,
                automatable: false
            });

        this.registerParameter(this.spreadParameter);

        this.oscillatorFrequencyParameter = new AbstractParameter(
            'Oscillator Frequency', this,
            (o, x) => [],
            {
                documentation: "Controls the root note for the synth.",
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0,
                automatable: false
            });

        this.registerParameter(this.oscillatorFrequencyParameter);

        // Detune of oscillator (in cents)
        this.detuneParameter = new PolyParameter(
            'Detune',
            this.voices.map(x => x.detune),
            {
                documentation: "Controls the detuning for this operator",
                min: -100,
                max: 100,
                unit: '',
                defaultValue: 0,
            });
        
        this.registerParameter(this.detuneParameter);

       // "Glide" or "Portamento" of synth
        this.portamentoParameter = new AbstractParameter(
            'Portamento',
            this, (o, x) => this.voices.forEach(
                v => v.portamento = x / PORTAMENTO_FACTOR)
            ,
            {
                documentation: "Controls the gliding between notes for this operator",
                min: 0,
                max: 5,
                unit: '',
                defaultValue: 0
            });
        
        this.registerParameter(this.portamentoParameter);

        // Polyphony, i.e. how many voices in the synth
        // 1 means monophonic synth
        this.polyphonyParameter = new IntegerParameter(
            'Polyphony',
            this, (o, x) => {
                this.onPolyphonyChanged(x);
            },
            {
                min: 0,
                max: 16,
                unit: '',
                defaultValue: 1,
                automatable: false
            });

        this.registerParameter(this.polyphonyParameter);
   }

    get enabled() {
        return this.enabledParameter.get(true);
    }

    set enabled(x) {
       this.enabledParameter.set(x);
    }

    get oscillatorLevel() {
        return this.oscillatorLevelParameter.get(true);
    }

    set oscillatorLevel(v) {
        return this.oscillatorLevelParameter.set(v);
    }

    get oscillatorFrequency() {
        return this.oscillatorFrequencyParameter.get(true);
    }

    set oscillatorFrequency(x) {
        this.oscillatorFrequencyParameter.set(x);
    }

    get detune() {
        return this.detuneParameter.get(true);
    }

    set detune(x) {
        this.detuneParameter.set(x);
    }

    get pulseWidth() {
        return this.pulseWidthParameter.get(true);
    }

    set pulseWidth(x) {
        this.pulseWidthParameter.set(x);
    }

    get portamento() {
        return this.portamentoParameter.get(true);
    }

    set portamento(p) {
        this.portamentoParameter.set(p);
    }

    set harmonics(x) {
        this.harmonicsParameter.set(x);
    }

    get harmonics() {
        return this.harmonicsParameter.get(true);
    }

    get polyphony() {
        if (this.polyphonyParameter === undefined) {
            return 8;
        } else {
            return this.polyphonyParameter.get(true);
        }
    }

    set polyphony(p) {
        this.polyphonyParameter.set(p, true, true);
    }

    registerParameter(parameter, signalGetter) {
        let b = parameter.setStore(this.store);
        this.bulk = {
            ... this.bulk,
            ... b
        };
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    registerContext(c) {
    }

    getParameters() {
        let parameters = this.parameters;
        for (let formant of this.formants) {
            parameters = [... parameters, ... formant.getParameters()];
        }
        return [
            ... parameters,
            ...this.ampEnvelope.parameters];
    }

    // refreshes the synth's parameters to their saved state
    refreshParameters() {
        if (!this.customOscillatorType) {
            this.oscillatorTypeParameter.set(this.oscillatorType, true, true);
            if (this.partials !== null) {
                this.partialsParameter.set(this.partials, true, true);
            }
        }
        this.customOscillatorTypeParameter.set(this.customOscillatorType, true, true);
        this.portamentoParameter.set(this.portamento, true, true);
        this.harmonicsParameter.set(this.harmonics, true, true);
        this.pulseWidthParameter.set(this.pulseWidth, true, true);;
    }

    get oscillatorOctave() {
        return this.oscillatorOctaveParameter.get();
    }
}
